import { useState } from "react";
import { motion } from "framer-motion";
import { Flag } from "lucide-react";
import Navigation from "@/components/landing/Navigation";
import Footer from "@/components/landing/Footer";
import { useTranslation } from "@/hooks/useTranslation";

const EUNationalProjects = () => {
  const [flippedCards, setFlippedCards] = useState<Set<string>>(new Set());
  
  const toggleFlip = (project: string) => {
    setFlippedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(project)) {
        newSet.delete(project);
      } else {
        newSet.add(project);
      }
      return newSet;
    });
  };

  const { t } = useTranslation();

  const portfolioProjects = [
    "EVOLVE2CARE",
    "CyberSuite",
    "CEREBRIS",
    "Marissa",
    "RAISE",
    "ValueCare",
    "InteLLIot",
    "INSPECt",
    "PAT",
    "IntelTriage",
    "Liver3D",
    "SEFAC",
    "CyberCare",
    "IM",
    "Smartcare",
  ];

  const projectUrls: Record<string, string> = {
    Marissa: "https://www.pharaon.eu/marissa-pharaon-second-open-call-winner/",
    RAISE: "https://raise.iti.gr/",
    ValueCare: "https://projectvaluecare.eu/",
    InteLLIot: "https://intelliot.eu/about",
    INSPECt: "http://inspect.vidavo.eu/",
    PAT: "https://www.ilsp.gr/en/projects/pat-en/",
    IntelTriage: "https://inteltriage.web.auth.gr/",
    SEFAC: "https://sefacproject.eu/",
    Smartcare: "https://pilotsmartcare.eu/home.html",
    CyberCare: "https://cybercare.gr/en/home-en/",
    EVOLVE2CARE: "https://evolve2care.eu/",
    CyberSuite: "https://cybersuiteproject.eu/",
    CEREBRIS: "https://www.cerebris.eu/",
  };

  const projectLogos: Record<string, string> = {
    Marissa: "/lovable-uploads/2710ec83-28a8-4213-93db-3323be0dc3fa.png",
    RAISE: "/lovable-uploads/664cd28b-4994-4158-b6ce-1fdaf5fd4f44.png",
    ValueCare: "/lovable-uploads/40b4bfb2-daa4-4c3c-83d7-4fb05997cddd.png",
    InteLLIot: "/lovable-uploads/77e9e4b5-612a-4c3d-b585-801d4a824192.png",
    INSPECt: "/lovable-uploads/f7795662-247d-42e8-b772-adaa9cbd3958.png",
    PAT: "/lovable-uploads/45b1b4af-5932-44a5-bc84-adfad5483b97.png",
    IntelTriage: "/lovable-uploads/97cfa4d2-87f1-4089-837a-c349a3a31030.png",
    SEFAC: "/lovable-uploads/0873dfc3-5892-453c-8951-7f3917398a51.png",
    CyberCare: "/lovable-uploads/1e05cb17-74db-437b-969e-3817f743d9b8.png",
    Smartcare: "/lovable-uploads/6f577250-6b34-4a86-ae62-981f83da955a.png",
    Liver3D: "/lovable-uploads/2ab3d047-5204-4656-aa01-c77dc859f55b.png",
    IM: "/lovable-uploads/2d9eb359-3503-46bf-9079-603106e0857c.png",
    EVOLVE2CARE: "/lovable-uploads/evolve2care-logo.png",
    CyberSuite: "/lovable-uploads/cybersuite-logo.png",
    CEREBRIS: "/lovable-uploads/cerebris-logo.png",
  };

  const getProjectDescription = (projectName: string): string => {
    const projectKey = projectName.toLowerCase();
    const keyMap: Record<string, keyof typeof t.euNationalProjects.projectDescriptions> = {
      marissa: "marissa",
      raise: "raise",
      valuecare: "valueCare",
      intelliot: "intelliot",
      inspect: "inspect",
      pat: "pat",
      inteltriage: "intelTriage",
      liver3d: "liver3d",
      sefac: "sefac",
      cybercare: "cyberCare",
      im: "im",
      smartcare: "smartcare",
      evolve2care: "evolve2care",
      cybersuite: "cyberSuite",
      cerebris: "cerebris",
    };

    const translationKey = keyMap[projectKey];
    return translationKey
      ? t.euNationalProjects.projectDescriptions[translationKey]
      : t.euNationalProjects.projectDescriptions.default;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-50 relative overflow-hidden">
      {/* Flowing background elements */}
      <div className="absolute top-0 right-0 w-1/2 h-[60%] bg-gradient-to-bl from-secondary/15 via-secondary/8 to-transparent blur-3xl" />
      <div className="absolute top-1/4 left-0 w-1/3 h-[50%] bg-gradient-to-r from-accent-teal/12 via-accent-teal/6 to-transparent blur-2xl" />
      <div className="absolute bottom-0 right-1/4 w-1/2 h-[40%] bg-gradient-to-t from-secondary/10 via-transparent to-transparent blur-3xl" />

      <Navigation />
      <main className="pt-32 pb-16 relative overflow-hidden">
        <div className="container-padding relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Hero */}
            <motion.div
              className="text-center mb-20"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div className="inline-flex items-center gap-2 bg-gradient-to-r from-secondary/10 to-accent-teal/10 border border-secondary/20 rounded-full px-6 py-3 text-secondary font-medium backdrop-blur-sm mb-6">
                <Flag className="w-4 h-4" />
                {t.euNationalProjects.badge}
              </motion.div>
              <motion.h1 className="heading-xl mb-8 py-[10px] text-accent-teal">
                {t.euNationalProjects.title}
              </motion.h1>
              <motion.p className="text-xl text-neutral-700 max-w-3xl mx-auto leading-relaxed py-[2px]">
                {t.euNationalProjects.subtitle}
              </motion.p>
            </motion.div>

            {/* Project Portfolio Grid */}
            <motion.div
              id="portfolio-section"
              className="mt-20"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {portfolioProjects.map((project, index) => {
                  const projectUrl = projectUrls[project];
                  const logoUrl = projectLogos[project];
                  const description = getProjectDescription(project);
                  const hasExternalLink = project !== "Liver3D" && project !== "IM" && projectUrl;

                    return (
                    <motion.div
                      key={project}
                      id={`${project}-card`}
                      className="group perspective-1000 h-96 cursor-pointer"
                      onClick={() => toggleFlip(project)}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.05 }}
                    >
                      <div className={`relative w-full h-full transform-style-preserve-3d transition-transform duration-700 will-change-transform ${
                        flippedCards.has(project) ? "rotate-y-180" : ""
                      } md:group-hover:rotate-y-180`}>
                        
                        {/* Front Face */}
                        <div className={`absolute inset-0 backface-hidden md:group-hover:pointer-events-none ${flippedCards.has(project) ? "pointer-events-none" : ""}`}>
                          <div className="healthcare-card border border-primary/10 h-full flex flex-col items-center justify-center p-6">
                            <div className="w-24 h-24 mb-6 group-hover:scale-110 transition-transform duration-300">
                              {logoUrl ? (
                                <img
                                  src={logoUrl}
                                  alt={`${project} Logo`}
                                  className={`w-full h-full object-contain ${project === "IM" ? "rounded-lg" : ""}`}
                                />
                              ) : (
                                <div className="w-full h-full rounded-lg bg-gradient-to-br from-secondary/10 to-accent-teal/10 border border-secondary/20 flex items-center justify-center">
                                  <div className="w-4 h-4 rounded-full bg-secondary/60"></div>
                                </div>
                              )}
                            </div>
                            <h3 className="font-semibold text-primary text-xl text-center">{project}</h3>
                          </div>
                        </div>
                        
                        {/* Back Face */}
                        <div className={`absolute inset-0 backface-hidden rotate-y-180 pointer-events-none md:group-hover:pointer-events-auto ${flippedCards.has(project) ? "pointer-events-auto" : ""}`}>
                          <div className="healthcare-card border border-primary/10 h-full flex flex-col p-5 bg-gradient-to-br from-primary/5 to-secondary/5">
                            <h3 className="font-semibold text-primary text-lg text-center mb-3 flex-shrink-0">{project}</h3>
                            
                            {/* Scrollable Description */}
                            <div
                              className="flex-1 overflow-y-auto pr-2 mb-4 nice-scrollbar"
                              style={{ transform: "translateZ(0)", WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
                              onWheel={(e) => {
                                const el = e.currentTarget;
                                const atTop = el.scrollTop === 0 && e.deltaY < 0;
                                const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 1 && e.deltaY > 0;
                                if (!atTop && !atBottom) {
                                  el.scrollTop += e.deltaY;
                                  e.stopPropagation();
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <p className="text-neutral-700 leading-relaxed text-sm text-center">
                                {description}
                              </p>
                            </div>
                            
                            {/* Button */}
                            {hasExternalLink && (
                            <div
                              className="button-secondary-nav w-full px-4 py-2 text-center text-sm flex-shrink-0 relative z-10 cursor-pointer"
                              data-external-link
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(projectUrl, '_blank');
                                }}
                              >
                                {t.euNationalProjects.seeMore}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default EUNationalProjects;
